/**
 * AI Models Admin Tab
 * 
 * Manage AI models: enable/disable, set tiers, sync from OpenRouter
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, Check, X, AlertTriangle, Zap, DollarSign, Eye, EyeOff } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const CAPABILITY_ICONS = {
  text: '💬',
  vision: '👁️',
  code: '💻',
  audio: '🔊',
  'image-gen': '🎨',
  reasoning: '🧠',
  embedding: '📊'
};

const TIER_COLORS = {
  budget: 'bg-green-500/20 text-green-400',
  standard: 'bg-blue-500/20 text-blue-400',
  premium: 'bg-purple-500/20 text-purple-400'
};

const AIModelsTab = ({ user }) => {
  const [models, setModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ enabled: 'all', capability: 'all', provider: 'all' });
  const [stats, setStats] = useState(null);
  const [toast, setToast] = useState(null);

  const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    global: { headers: { Authorization: `Bearer ${user?.accessToken}` } }
  });

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch all models (including disabled) with service role
      const response = await fetch('/.netlify/functions/admin-ai-models', {
        headers: { 'Authorization': `Bearer ${user?.accessToken}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch models');
      }

      const data = await response.json();
      setModels(data.models || []);
      setStats(data.stats);
    } catch (err) {
      console.error('Fetch models error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      setError(null);

      const response = await fetch('/.netlify/functions/sync-ai-models', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user?.accessToken}` }
      });

      if (!response.ok) {
        throw new Error('Sync failed');
      }

      const result = await response.json();
      setToast({ type: 'success', message: `Sync complete: ${result.updated} updated, ${result.new} new models` });
      setTimeout(() => setToast(null), 5000);

      // Refresh the list
      await fetchModels();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const toggleEnabled = async (modelId, enabled) => {
    try {
      const response = await fetch('/.netlify/functions/admin-ai-models', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${user?.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ modelId, enabled })
      });

      if (!response.ok) {
        throw new Error('Failed to update model');
      }

      setModels(models.map(m => 
        m.id === modelId ? { ...m, enabled } : m
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const updateTier = async (modelId, tier) => {
    try {
      const response = await fetch('/.netlify/functions/admin-ai-models', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${user?.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ modelId, tier })
      });

      if (!response.ok) {
        throw new Error('Failed to update tier');
      }

      setModels(models.map(m => 
        m.id === modelId ? { ...m, tier } : m
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  // Filter models
  const filteredModels = models.filter(m => {
    if (filter.enabled !== 'all' && m.enabled !== (filter.enabled === 'true')) return false;
    if (filter.capability !== 'all' && !m.capabilities?.includes(filter.capability)) return false;
    if (filter.provider !== 'all' && m.provider !== filter.provider) return false;
    return true;
  });

  // Get unique providers
  const providers = [...new Set(models.map(m => m.provider))].sort();
  const capabilities = ['text', 'vision', 'code', 'audio', 'image-gen', 'reasoning', 'embedding'];

  const formatPrice = (price) => {
    if (!price) return '-';
    const perMillion = price * 1000000;
    if (perMillion < 1) return `$${perMillion.toFixed(3)}/1M`;
    return `$${perMillion.toFixed(2)}/1M`;
  };

  const formatCredits = (credits) => {
    if (!credits) return '-';
    if (credits < 0.001) return '<0.001';
    if (credits < 0.01) return credits.toFixed(4);
    return credits.toFixed(3);
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Loading AI models...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header with stats and actions */}
      <div className="p-4 border-b border-gray-800 bg-gray-800/50">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-gray-400">Total:</span>
              <span className="ml-2 text-white font-medium">{models.length}</span>
            </div>
            <div>
              <span className="text-gray-400">Enabled:</span>
              <span className="ml-2 text-green-400 font-medium">{models.filter(m => m.enabled).length}</span>
            </div>
            {stats?.usage24h !== undefined && (
              <div>
                <span className="text-gray-400">Usage (24h):</span>
                <span className="ml-2 text-cyan-400 font-medium">{stats.usage24h} requests</span>
              </div>
            )}
          </div>
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync from OpenRouter'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-gray-800 flex flex-wrap gap-3">
        <select
          value={filter.enabled}
          onChange={e => setFilter({ ...filter, enabled: e.target.value })}
          className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white"
        >
          <option value="all">All Status</option>
          <option value="true">Enabled</option>
          <option value="false">Disabled</option>
        </select>

        <select
          value={filter.provider}
          onChange={e => setFilter({ ...filter, provider: e.target.value })}
          className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white"
        >
          <option value="all">All Providers</option>
          {providers.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <select
          value={filter.capability}
          onChange={e => setFilter({ ...filter, capability: e.target.value })}
          className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white"
        >
          <option value="all">All Capabilities</option>
          {capabilities.map(c => (
            <option key={c} value={c}>{CAPABILITY_ICONS[c]} {c}</option>
          ))}
        </select>

        <span className="text-gray-400 text-sm ml-auto">
          Showing {filteredModels.length} of {models.length}
        </span>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`p-3 border-b flex items-center justify-between text-sm ${
          toast.type === 'success'
            ? 'bg-green-500/10 border-green-500/20 text-green-400'
            : 'bg-red-500/10 border-red-500/20 text-red-400'
        }`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-4 hover:opacity-70">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="overflow-auto max-h-[60vh]">
        <table className="w-full text-sm">
          <thead className="bg-gray-800 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Enabled</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Model</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Provider</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Capabilities</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Tier</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Input Price</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Output Price</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Our Credits/1K</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Context</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {filteredModels.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                  No models match filters
                </td>
              </tr>
            ) : (
              filteredModels.map(model => (
                <tr 
                  key={model.id} 
                  className={`hover:bg-gray-800/50 ${!model.enabled ? 'opacity-60' : ''}`}
                >
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleEnabled(model.id, !model.enabled)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        model.enabled 
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                          : 'bg-gray-700 text-gray-500 hover:bg-gray-600'
                      }`}
                    >
                      {model.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{model.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{model.id}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{model.provider}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {(model.capabilities || ['text']).map(cap => (
                        <span 
                          key={cap} 
                          title={cap}
                          className="text-sm"
                        >
                          {CAPABILITY_ICONS[cap] || '❓'}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <select
                      value={model.tier || 'standard'}
                      onChange={e => updateTier(model.id, e.target.value)}
                      className={`px-2 py-1 rounded text-xs font-medium ${TIER_COLORS[model.tier] || TIER_COLORS.standard} bg-transparent border-0 cursor-pointer`}
                    >
                      <option value="budget">Budget</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400 font-mono text-xs">
                    {formatPrice(model.openrouter_prompt_price)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400 font-mono text-xs">
                    {formatPrice(model.openrouter_completion_price)}
                  </td>
                  <td className="px-4 py-3 text-right text-cyan-400 font-mono text-xs">
                    {formatCredits(model.credits_per_1k_output)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400 text-xs">
                    {model.context_length ? `${(model.context_length / 1000).toFixed(0)}K` : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AIModelsTab;
